import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase, fetchUserCredits, getAdminRole, fetchImagemotionActivationState, isAbortError } from './services/supabase';
import type { AdminRole, ImagemotionActivationState } from './services/supabase';
import { Loader2 } from 'lucide-react';
import { creditBalanceRange, trackImagemotionEvent } from './services/analytics';
import {
  clearPostAuthNext,
  parseAllowedNext,
  persistPostAuthNext,
  readPostAuthNext,
  viewForNext,
} from './lib/marketing-intent';

// Components
import FluidBackground from './components/FluidBackground';
import AuthDialog from './components/AuthDialog';
import Header from './components/Header';
import Footer from './components/Footer';
import Login from './components/Login';
import LandingPage from './components/LandingPage';
import Dashboard from './components/Dashboard';
import NewProject from './components/NewProject';
import ProjectDetail from './components/ProjectDetail';
import ReferralsPage from './components/ReferralsPage';
import { ReferralPromoPopup } from '@rehub/announcements';
import BuyCreditsModal from './components/BuyCreditsModal';
import SubscriptionPlansModal from './components/SubscriptionPlansModal';
import SharedVideoViewer from './components/SharedVideoViewer';
import AdminPanel from './components/AdminPanel';
import ListingFunnelRouter from './components/listing-funnel/ListingFunnelRouter';
import StagingAnimation from './components/StagingAnimation';
import { FUNNEL_BASE_PATH } from './components/listing-funnel/constants';

type AppView = 'LOADING' | 'LOGIN' | 'DASHBOARD' | 'NEW_PROJECT' | 'PROJECT_DETAIL' | 'STAGING_ANIMATION';

const SESSION_VIEW_KEY = 'im_app_view';
const SESSION_PROJECT_KEY = 'im_selected_project';

function persistView(view: AppView, projectId?: string | null) {
  try {
    if (view === 'LOADING' || view === 'LOGIN') return;
    sessionStorage.setItem(SESSION_VIEW_KEY, view);
    if (projectId) sessionStorage.setItem(SESSION_PROJECT_KEY, projectId);
    else if (view !== 'PROJECT_DETAIL') sessionStorage.removeItem(SESSION_PROJECT_KEY);
  } catch {}
}

function restoreView(): { view: AppView; projectId: string | null } | null {
  try {
    const view = sessionStorage.getItem(SESSION_VIEW_KEY) as AppView | null;
    if (!view || view === 'LOADING' || view === 'LOGIN') return null;
    if (view === 'NEW_PROJECT') return { view: 'DASHBOARD', projectId: null };
    const projectId = sessionStorage.getItem(SESSION_PROJECT_KEY);
    if (view === 'PROJECT_DETAIL' && !projectId) return { view: 'DASHBOARD', projectId: null };
    return { view, projectId };
  } catch {
    return null;
  }
}

function getShareToken(): string | null {
  const match = window.location.pathname.match(/^\/view\/([a-zA-Z0-9]+)$/);
  return match ? match[1] : null;
}

// Internal deep-link: /p/{projectId} opens that project's detail view for the
// signed-in team, so a project URL can be copied from the address bar and
// shared internally (or bookmarked / opened in a new tab).
function getDeepLinkProjectId(): string | null {
  const match = window.location.pathname.match(/^\/p\/([0-9a-fA-F-]+)$/);
  return match ? match[1] : null;
}

function isStagingAnimationRoute(): boolean {
  return window.location.pathname === '/staging-animation';
}

function isListingFunnelRoute(): boolean {
  return window.location.pathname.startsWith(FUNNEL_BASE_PATH);
}

async function authCallWithRetry<T>(fn: () => Promise<T>, retries = 3): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (!isAbortError(err) || attempt === retries - 1) throw err;
      await new Promise((r) => setTimeout(r, 150 * (attempt + 1)));
    }
  }
  throw lastErr;
}

export default function App() {
  // Listing video funnel — fully public, self-contained routing
  if (isListingFunnelRoute()) {
    return <ListingFunnelRouter />;
  }

  const shareToken = getShareToken();
  if (shareToken) {
    return <SharedVideoViewer shareToken={shareToken} />;
  }

  const [view, setView] = useState<AppView>('LOADING');
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [userCredits, setUserCredits] = useState(0);
  const [showReferrals, setShowReferrals] = useState(false);
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
  const [showSubscriptionPlans, setShowSubscriptionPlans] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [authCallbackError, setAuthCallbackError] = useState<string | null>(null);
  const [adminRole, setAdminRole] = useState<AdminRole | null>(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [activation, setActivation] = useState<ImagemotionActivationState | null>(null);
  const [buyCreditsHighlightOffer, setBuyCreditsHighlightOffer] = useState(false);
  const intentionalSignOutRef = useRef(false);
  const hasInitializedRef = useRef(false);

  // --- Credits + Admin ---
  const loadCredits = useCallback(async (userId: string) => {
    try {
      const [credits, role, activationState] = await Promise.all([
        fetchUserCredits(userId),
        getAdminRole(userId),
        fetchImagemotionActivationState(userId),
      ]);
      setUserCredits(credits);
      setAdminRole(role);
      if (activationState.success) setActivation(activationState);
    } catch (err) {
      console.warn('Failed to fetch credits:', err);
    }
  }, []);

  const openBuyCredits = useCallback((opts?: { highlightOffer?: boolean; source?: string }) => {
    if (user) {
      trackImagemotionEvent('buy_credits_clicked', user.id, {
        credit_balance_range: creditBalanceRange(userCredits),
        offer_eligible: Boolean(activation?.double_credit_eligible),
        tester_eligible: Boolean(activation?.tester_eligible),
        source: opts?.source || 'header',
      });
    }
    setBuyCreditsHighlightOffer(Boolean(opts?.highlightOffer));
    setShowBuyCreditsModal(true);
  }, [user, userCredits, activation]);

  // --- Navigation ---
  // Reflect an open project in the address bar as /p/{id} (and reset to / when
  // not viewing a project) so the URL is a shareable internal project link.
  const syncProjectUrl = useCallback((target: AppView, projectId?: string | null) => {
    if (target === 'STAGING_ANIMATION') return;
    const path = target === 'PROJECT_DETAIL' && projectId ? `/p/${projectId}` : '/';
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
    }
  }, []);

  const navigate = useCallback((target: AppView, projectId?: string) => {
    if (projectId) setSelectedProjectId(projectId);
    setView(target);
    persistView(target, projectId);
    syncProjectUrl(target, projectId);
  }, [syncProjectUrl]);

  // Keep the view in sync with browser back/forward across /p/{id} links.
  useEffect(() => {
    if (!user) return;
    const onPop = () => {
      const pid = getDeepLinkProjectId();
      if (pid) {
        setSelectedProjectId(pid);
        setView('PROJECT_DETAIL');
      } else if (isStagingAnimationRoute()) {
        setView('STAGING_ANIMATION');
      } else {
        setView((cur) => (cur === 'PROJECT_DETAIL' || cur === 'STAGING_ANIMATION' ? 'DASHBOARD' : cur));
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [user]);

  // --- Auth Initialization ---
  useEffect(() => {
    let cancelled = false;
    let recoveryTimer: ReturnType<typeof setTimeout> | null = null;
    let callbackTimer: ReturnType<typeof setTimeout> | null = null;

    // Land the authenticated user on the right screen. A /p/{id} deep link
    // wins, then any session-restored view, otherwise the dashboard.
    const resolveInitialView = () => {
      hasInitializedRef.current = true;
      const deepLinkId = getDeepLinkProjectId();
      if (deepLinkId) {
        setSelectedProjectId(deepLinkId);
        setView('PROJECT_DETAIL');
        return;
      }
      if (isStagingAnimationRoute()) {
        setView('STAGING_ANIMATION');
        return;
      }
      const marketingView = viewForNext(readPostAuthNext());
      if (marketingView) {
        clearPostAuthNext();
        setView(marketingView);
        persistView(marketingView);
        syncProjectUrl(marketingView, null);
        return;
      }
      const restored = restoreView();
      if (restored) {
        if (restored.projectId) setSelectedProjectId(restored.projectId);
        setView(restored.view);
      } else {
        setView('DASHBOARD');
      }
    };

    const initAuth = async () => {
      const params = new URLSearchParams(window.location.search);
      const nextFromUrl = parseAllowedNext(params.get('next'));
      if (nextFromUrl) persistPostAuthNext(nextFromUrl);

      // 1. Check for SSO tokens from cross-app navigation
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const ssoAccessToken = hashParams.get('sso_access_token');
      const ssoRefreshToken = hashParams.get('sso_refresh_token');

      if (ssoAccessToken && ssoRefreshToken) {
        console.log('SSO tokens detected, setting session...');
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);

        try {
          const { data, error } = await supabase.auth.setSession({
            access_token: ssoAccessToken,
            refresh_token: ssoRefreshToken,
          });

          if (data.session && !cancelled) {
            console.log('SSO session established');
            setSession(data.session);
            setUser(data.session.user);
            loadCredits(data.session.user.id);
            resolveInitialView();
            return;
          }
          if (error) console.warn('SSO session failed:', error.message);
        } catch (err) {
          console.warn('SSO session error:', err);
        }
      }

      // 2. Check for auth callback (magic link redirect)
      const isAuthCallback = window.location.pathname === '/auth/callback';
      if (isAuthCallback) {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const callbackError = hashParams.get('error_description') || hashParams.get('error');
        if (callbackError) {
          console.warn('Auth callback error:', callbackError);
          window.history.replaceState({}, document.title, '/');
          const friendlyMessages: Record<string, string> = {
            'access_denied': 'Access was denied. Please try again.',
            'server_error': 'A server error occurred. Please try again.',
            'Email link is invalid or has expired': 'Your login link has expired. Please request a new one.',
          };
          setAuthCallbackError(friendlyMessages[callbackError] ?? callbackError.replace(/\+/g, ' '));
          setView('LOGIN');
          return;
        }

        callbackTimer = setTimeout(() => {
          if (!cancelled) {
            console.warn('Auth callback timeout');
            window.history.replaceState({}, document.title, '/');
            setView('LOGIN');
          }
        }, 8000);
        return;
      }

      // 3. Check for existing session and validate it with the server
      try {
        const { data: { session: existingSession } } = await authCallWithRetry(() => supabase.auth.getSession());
        if (cancelled) return;

        if (!existingSession) {
          setView('LOGIN');
          return;
        }

        // Validate the JWT is still accepted by the server (getSession only reads cache)
        const { data: { user: validatedUser }, error: userError } = await authCallWithRetry(() => supabase.auth.getUser());
        if (cancelled) return;

        if (validatedUser && !userError) {
          setSession(existingSession);
          setUser(validatedUser);
          loadCredits(validatedUser.id);
          resolveInitialView();
        } else {
          // Cached token expired — attempt a refresh before giving up
          const { data: refreshData } = await supabase.auth.refreshSession();
          if (cancelled) return;

          if (refreshData?.session) {
            setSession(refreshData.session);
            setUser(refreshData.session.user);
            loadCredits(refreshData.session.user.id);
            resolveInitialView();
          } else {
            console.warn('Session expired and refresh failed');
            setView('LOGIN');
          }
        }
      } catch (err) {
        if (!isAbortError(err)) console.warn('Auth init error:', err);
        if (!cancelled) setView('LOGIN');
      }
    };

    initAuth();

    // 4. Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        if (cancelled) return;

        switch (event) {
          case 'SIGNED_IN':
            // Cancel the /auth/callback safety timeout — otherwise a successful
            // Google/magic-link sign-in still gets forced to LOGIN ~8s later.
            if (callbackTimer) {
              clearTimeout(callbackTimer);
              callbackTimer = null;
            }
            setSession(newSession);
            setUser(newSession?.user ?? null);
            if (window.location.pathname === '/auth/callback') {
              window.history.replaceState({}, document.title, '/');
            }
            if (newSession) {
              loadCredits(newSession.user.id);
              const createdAt = Date.parse(newSession.user.created_at || '');
              if (Number.isFinite(createdAt) && Date.now() - createdAt < 15 * 60 * 1000) {
                try {
                  if (sessionStorage.getItem('im_signup_tracked') !== newSession.user.id) {
                    sessionStorage.setItem('im_signup_tracked', newSession.user.id);
                    trackImagemotionEvent('imagemotion_signup_completed', newSession.user.id, {
                      source: 'auth',
                    });
                  }
                } catch {
                  trackImagemotionEvent('imagemotion_signup_completed', newSession.user.id, {
                    source: 'auth',
                  });
                }
              }
              const pendingRef = localStorage.getItem('referral_code');
              if (pendingRef) {
                localStorage.removeItem('referral_code');
                const provider = newSession.user?.app_metadata?.provider;
                const authMethod =
                  provider === 'google' ? 'google' : provider === 'email' ? 'magic_link' : null;
                void supabase
                  .rpc('apply_referral_code', {
                    p_code: pendingRef,
                    p_auth_method: authMethod,
                    p_app_source: 'imagemotion',
                  })
                  .catch(() => {});
              }
            }
            if (!hasInitializedRef.current) {
              resolveInitialView();
            }
            break;

          case 'TOKEN_REFRESHED':
            // Silently update session — never change the view
            if (recoveryTimer) {
              clearTimeout(recoveryTimer);
              recoveryTimer = null;
            }
            setSession(newSession);
            setUser(newSession?.user ?? null);
            if (newSession) loadCredits(newSession.user.id);
            break;

          case 'SIGNED_OUT':
            if (intentionalSignOutRef.current) {
              intentionalSignOutRef.current = false;
              setSession(null);
              setUser(null);
              setView('LOGIN');
              break;
            }
            // Unexpected sign-out (auto-refresh / cross-app token rotation).
            // Keep UI, then recover from shared cookie/localStorage.
            if (recoveryTimer) clearTimeout(recoveryTimer);
            recoveryTimer = setTimeout(async () => {
              for (let attempt = 1; attempt <= 8; attempt++) {
                if (cancelled) return;
                try {
                  const { data: { session: recovered } } = await supabase.auth.getSession();
                  if (recovered?.access_token) {
                    const { data: { user: ok } } = await supabase.auth.getUser();
                    if (ok) {
                      console.log(`Session recovered after transient SIGNED_OUT (attempt ${attempt})`);
                      setSession(recovered);
                      setUser(recovered.user);
                      loadCredits(recovered.user.id);
                      return;
                    }
                  }
                  const { data: { session: refreshed } } = await supabase.auth.refreshSession();
                  if (refreshed) {
                    setSession(refreshed);
                    setUser(refreshed.user);
                    loadCredits(refreshed.user.id);
                    return;
                  }
                } catch { /* retry */ }
                await new Promise((r) => setTimeout(r, 1500));
              }
              if (cancelled) return;
              setSession(null);
              setUser(null);
              setView('LOGIN');
            }, 500);
            break;

          case 'INITIAL_SESSION':
            // Only update state — initAuth validates and navigates after getUser()
            if (newSession) {
              setSession(newSession);
              setUser(newSession.user);
            }
            break;
        }
      }
    );

    return () => {
      cancelled = true;
      if (recoveryTimer) clearTimeout(recoveryTimer);
      if (callbackTimer) clearTimeout(callbackTimer);
      subscription.unsubscribe();
    };
  }, [loadCredits]);

  const handleSignOut = async () => {
    intentionalSignOutRef.current = true;
    hasInitializedRef.current = false;
    try { sessionStorage.removeItem(SESSION_VIEW_KEY); sessionStorage.removeItem(SESSION_PROJECT_KEY); } catch {}
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setView('LOGIN');
  };

  // Auto-open login modal when there's an auth callback error
  useEffect(() => {
    if (view === 'LOGIN' && authCallbackError && !showLoginModal) {
      setShowLoginModal(true);
    }
  }, [view, authCallbackError, showLoginModal]);

  // Marketing-site deep links: ?signup=1 | ?signin=1 | ?next=new|dashboard|staging
  useEffect(() => {
    if (view === 'LOADING') return;
    const params = new URLSearchParams(window.location.search);
    const wantsSignup = params.get('signup') === '1';
    const wantsSignin = params.get('signin') === '1';
    const next = parseAllowedNext(params.get('next'));
    if (!wantsSignup && !wantsSignin && !next) return;

    if (next) persistPostAuthNext(next);

    params.delete('signup');
    params.delete('signin');
    params.delete('next');
    const clean = `${window.location.pathname}${params.toString() ? `?${params}` : ''}${window.location.hash}`;
    window.history.replaceState({}, document.title, clean);

    if (user) {
      const target = viewForNext(readPostAuthNext());
      if (target) {
        clearPostAuthNext();
        navigate(target);
      }
      return;
    }

    if (wantsSignup || next === 'new' || next === 'staging') {
      setAuthMode('signup');
      setShowLoginModal(true);
    } else if (wantsSignin) {
      setAuthMode('login');
      setShowLoginModal(true);
    }
  }, [view, user, navigate]);

  // --- Loading State ---
  if (view === 'LOADING') {
    return (
      <div className="min-h-screen relative overflow-hidden bg-[#050509]">
        <FluidBackground />
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <div className="text-center animate-fade-in">
            <img src="/Logo.webp" alt="ImageMotion" width={624} height={85} className="h-8 sm:h-10 w-auto max-w-[min(80vw,300px)] mx-auto mb-4 object-contain" />
            <Loader2 className="w-6 h-6 mx-auto text-slate-400 animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  // --- Landing + Login Modal ---
  if (view === 'LOGIN') {
    return (
      <div className="min-h-screen flex flex-col bg-[#050509] relative">
        <FluidBackground />
        <div id="public-entry" className="relative z-10 flex flex-col flex-1 min-h-0">
          <Header
            appearance="entry"
            onReset={() => {}}
            showNewProject={false}
            onNewProject={() => {}}
            onSignOut={() => {}}
            onLogin={() => { setAuthMode('login'); setShowLoginModal(true); }}
            onSignUp={() => { persistPostAuthNext('new'); setAuthMode('signup'); setShowLoginModal(true); }}
            onOpenApp={() => navigate('DASHBOARD')}
            userEmail={user?.email}
          />
          <LandingPage
            isAuthenticated={Boolean(user)}
            onOpenApp={() => navigate('DASHBOARD')}
            onSignUp={() => { persistPostAuthNext('new'); setAuthMode('signup'); setShowLoginModal(true); }}
            onLogin={() => { setAuthMode('login'); setShowLoginModal(true); }}
            onOpenStaging={() => {
              if (user) {
                window.history.pushState({}, '', '/staging-animation');
                navigate('STAGING_ANIMATION');
                return;
              }
              persistPostAuthNext('staging');
              setAuthMode('signup');
              setShowLoginModal(true);
            }}
          />
          <div className="shrink-0">
            <Footer />
          </div>
        </div>
        {showLoginModal && (
          <AuthDialog onClose={() => { setShowLoginModal(false); setAuthCallbackError(null); }}>
            <Login
              notice={authCallbackError}
              onAuthSuccess={() => {
                setShowLoginModal(false);
                setAuthCallbackError(null);
                const pid = getDeepLinkProjectId();
                if (pid) {
                  setSelectedProjectId(pid);
                  setView('PROJECT_DETAIL');
                  return;
                }
                const marketingView = viewForNext(readPostAuthNext());
                if (marketingView) {
                  clearPostAuthNext();
                  navigate(marketingView);
                  return;
                }
                setView('DASHBOARD');
              }}
              onClose={() => { setShowLoginModal(false); setAuthCallbackError(null); }}
              initialMode={authMode}
            />
          </AuthDialog>
        )}
      </div>
    );
  }

  // --- Authenticated Layout ---
  const showNewProject = view === 'DASHBOARD';

  return (
    <div className="min-h-screen flex flex-col bg-black">
      <Header
        onReset={() => navigate('DASHBOARD')}
        showNewProject={showNewProject}
        userEmail={user?.email}
        userCredits={userCredits}
        onNewProject={() => navigate('NEW_PROJECT')}
        onSignOut={handleSignOut}
        onNavigateToReferrals={() => setShowReferrals(true)}
        onOpenBuyCredits={() => openBuyCredits({ source: 'header' })}
        onOpenPlans={() => setShowSubscriptionPlans(true)}
        isAdmin={!!adminRole}
        onOpenAdmin={() => setShowAdminPanel(true)}
      />

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        {view === 'DASHBOARD' && user && (
          <Dashboard
            userId={user.id}
            onNavigate={navigate}
            onOpenStagingAnimation={(jobId) => {
              const path = jobId ? `/staging-animation?id=${jobId}` : '/staging-animation';
              window.history.pushState({}, '', path);
              setView('STAGING_ANIMATION');
              persistView('STAGING_ANIMATION');
            }}
          />
        )}
        {view === 'STAGING_ANIMATION' && user && (
          <StagingAnimation
            userId={user.id}
            onBack={() => navigate('DASHBOARD')}
            onCreditsUpdated={() => loadCredits(user.id)}
          />
        )}
        {view === 'NEW_PROJECT' && user && (
          <NewProject
            userId={user.id}
            userCredits={userCredits}
            onNavigate={navigate}
            onCreditsUpdated={setUserCredits}
            onOpenPlans={() => setShowSubscriptionPlans(true)}
            onOpenBuyCredits={() => openBuyCredits({ source: 'new_project' })}
            testerEligible={Boolean(activation?.tester_eligible)}
            doubleCreditEligible={Boolean(activation?.double_credit_eligible)}
            isNewUser={Boolean(activation?.is_new_user)}
            onActivationRefresh={() => loadCredits(user.id)}
          />
        )}
        {view === 'PROJECT_DETAIL' && selectedProjectId && (
          <ProjectDetail
            projectId={selectedProjectId}
            userId={user?.id}
            isAdmin={!!adminRole}
            onNavigate={navigate}
            onOpenPlans={() => setShowSubscriptionPlans(true)}
            onOpenBuyCredits={() => openBuyCredits({ highlightOffer: true, source: 'project_detail' })}
            doubleCreditEligible={Boolean(activation?.double_credit_eligible)}
            onActivationRefresh={() => user && loadCredits(user.id)}
            userCredits={user ? userCredits : undefined}
          />
        )}
      </main>

      <Footer />

      {showReferrals && user && (
        <ReferralsPage userId={user.id} onClose={() => setShowReferrals(false)} />
      )}

      <ReferralPromoPopup
        client={supabase}
        userId={user?.id}
        appSource="imagemotion"
        onInvite={() => setShowReferrals(true)}
        suppressed={showReferrals || showBuyCreditsModal || showAdminPanel}
      />

      {showBuyCreditsModal && user && (
        <BuyCreditsModal
          userId={user.id}
          userCredits={userCredits}
          doubleCreditEligible={Boolean(activation?.double_credit_eligible)}
          highlightOffer={buyCreditsHighlightOffer}
          onClose={() => {
            setShowBuyCreditsModal(false);
            setBuyCreditsHighlightOffer(false);
            loadCredits(user.id);
          }}
        />
      )}

      {showSubscriptionPlans && user && (
        <SubscriptionPlansModal
          userId={user.id}
          onClose={() => setShowSubscriptionPlans(false)}
        />
      )}

      {showAdminPanel && adminRole && (
        <AdminPanel adminRole={adminRole} onClose={() => setShowAdminPanel(false)} />
      )}
    </div>
  );
}
